// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor (Task #25).

import { ADDRESS_NAME_PLACEHOLDER } from "../../services/addressName";
import { generatePassword } from "../../utils/password";
import { formatPhone, isValidEmail } from "../../utils/sanitize";
import { Btn } from "../../utils/ui.jsx";
import { AddressAutocomplete } from "../form/AddressAutocomplete.jsx";
import { AddressNameHelp } from "../form/AddressNameHelp.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

const ROLE_LABELS = {
  staff: "Responder",
  picker: "Picker",
  admin: "Supervisor",
  superuser: "SuperUser",
  combined: "Combined",
};
function roleLabel(r) {
  return ROLE_LABELS[r] || r;
}

// A @zephyreducation.com.au staff member signs in with Google Workspace SSO and
// doesn't need a welcome/set-password email; everyone else uses password + the
// authenticator and should get the account email. Both are just defaults derived
// from the email domain — the admin can override either control.
function isZephyrDomain(email) {
  return /@zephyreducation\.com\.au$/i.test((email || "").trim());
}

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export function NewUserModal({
  open,
  onClose,
  // What this modal creates: "shelters" | "caseworkers" | "staff". Decoupled
  // from the list's activeSection (Phase 3) so the shelter detail's Users tab
  // can open a caseworker form without a Caseworkers tab existing.
  kind,
  // When set (a shelter_code), the caseworker form's Shelter select is locked
  // to it — used by the shelter detail's "+ New User".
  lockShelter,
  newForm,
  setNewForm,
  newSaving,
  newErr,
  setNewErr,
  showPass,
  setShowPass,
  createUser,
  shelters,
  customerTypes,
  salesGroups,
  mapsReadyTick,
}) {
  if (!open) return null;
  // Domain-derived defaults for the Zephyr Staff form; each is overridable and
  // "sticks" once the admin sets it (login_method is a truthy string; send_welcome
  // is compared with === undefined so an explicit false is respected).
  const isZephyr = isZephyrDomain(newForm.email);
  const sendWelcome = newForm.send_welcome === undefined ? !isZephyr : newForm.send_welcome;
  // Contact-only (No Login) caseworker: no auth account is created, so the password
  // field is hidden and the email becomes optional (a contact can have no email).
  const noLoginContact = kind === "caseworkers" && newForm.no_login === true;
  const dragTitle = `${
    kind === "shelters"
      ? "New DFV Service"
      : kind === "staff"
        ? "New Zephyr Staff"
        : noLoginContact
          ? "New Contact (no login)"
          : "New Caseworker"
  } — drag to move`;
  return (
    <ModalBackdrop
      onClose={onClose}
      draggable
      dragTitle={dragTitle}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 620,
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <div style={{ padding: 24 }}>
        {/* CASEWORKER form */}
        {kind === "caseworkers" && (
          <div>
            <div className="font-bold text-[16px] text-zephyr-teal-dark mb-1">
              {noLoginContact ? "New Contact (no login)" : "New Caseworker"}
            </div>
            <div className="text-[13px] text-zephyr-muted mb-4">
              {noLoginContact
                ? "Creates a contact record on this DFV Service with NO login account. You can give them a login later from their edit form."
                : "Creates a login account linked to an existing DFV Service via DFV Service Code."}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  First Name
                </label>
                <input
                  value={newForm.first_name}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { first_name: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Last Name
                </label>
                <input
                  value={newForm.last_name}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { last_name: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  {noLoginContact ? "Email" : "Email * (login)"}
                </label>
                <input
                  value={newForm.email}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { email: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="user@example.com"
                />
              </div>
              {!noLoginContact && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px]">
                      Password *
                    </label>
                    <button
                      type="button"
                      onClick={function () {
                        const pw = generatePassword();
                        setNewForm(function (f) {
                          return Object.assign({}, f, { password: pw });
                        });
                        setShowPass(true);
                      }}
                      className="text-[11px] font-bold text-zephyr-teal-dark bg-white border border-zephyr-border rounded-[6px] px-2 py-[2px] cursor-pointer hover:bg-[#EEF3F2]"
                      title="Fill a strong random password (min 12 chars: upper, lower, number, symbol)"
                    >
                      Generate
                    </button>
                  </div>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPass ? "text" : "password"}
                      value={newForm.password}
                      onChange={function (e) {
                        setNewForm(function (f) {
                          return Object.assign({}, f, { password: e.target.value });
                        });
                      }}
                      className="w-full py-2 px-10 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                      style={{ fontFamily: "inherit" }}
                      placeholder="Min 12 chars"
                    />
                    <button
                      type="button"
                      onClick={function () {
                        setShowPass(function (v) {
                          return !v;
                        });
                      }}
                      className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-[16px] text-zephyr-muted p-0"
                    >
                      {showPass ? "🙈" : "👁"}
                    </button>
                  </div>
                  <div className="text-[11px] text-zephyr-muted mt-[3px]">
                    Min 12 chars · upper · lower · number · symbol
                  </div>
                </div>
              )}
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  DFV Service *
                </label>
                <select
                  value={newForm.shelter_code}
                  disabled={!!lockShelter}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { shelter_code: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{
                    fontFamily: "inherit",
                    ...(lockShelter ? { background: "#EEF3F2", color: "#607D79" } : {}),
                  }}
                >
                  <option value="">— Select a DFV Service —</option>
                  {shelters.map(function (s) {
                    return (
                      <option key={s.shelter_code} value={s.shelter_code}>
                        {s.shelter_name} ({s.shelter_code})
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Phone
                </label>
                <input
                  value={newForm.phone || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { phone: e.target.value });
                    });
                  }}
                  onBlur={function (e) {
                    const v = formatPhone(e.target.value, "landline");
                    setNewForm(function (f) {
                      return Object.assign({}, f, { phone: v });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="e.g. 07 3219 5644"
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Mobile
                </label>
                <input
                  value={newForm.mobile || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { mobile: e.target.value });
                    });
                  }}
                  onBlur={function (e) {
                    const v = formatPhone(e.target.value, "mobile");
                    setNewForm(function (f) {
                      return Object.assign({}, f, { mobile: v });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="e.g. 0412 345 678"
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Primary Contact Role
                </label>
                <select
                  value={newForm.primary_contact_label || "DFV Service Manager"}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { primary_contact_label: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                >
                  <option value="DFV Service Manager">DFV Service Manager</option>
                  <option value="Caseworker">Caseworker</option>
                  <option value="Admin Officer">Admin Officer</option>
                  <option value="Coordinator">Coordinator</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="col-span-full">
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newForm.no_login === true}
                    onChange={function (e) {
                      setNewForm(function (f) {
                        return Object.assign({}, f, { no_login: e.target.checked });
                      });
                    }}
                  />
                  <span>No login account (contact only)</span>
                </label>
                <div className="text-[11px] text-zephyr-muted mt-1 ml-6">
                  Tick to add this person as a contact on this DFV Service with no sign-in. You can
                  give them a login later from their edit form.
                </div>
              </div>
              {!noLoginContact &&
                !(newForm.first_name || "").trim() &&
                !(newForm.last_name || "").trim() && (
                  <div className="col-span-full">
                    <label className="flex items-center gap-2 text-[13px]">
                      <input type="checkbox" checked={true} readOnly />
                      <span className="text-zephyr-muted text-[12px]">
                        No name entered — will be created as a shared/generic account
                      </span>
                    </label>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* ZEPHYR STAFF form */}
        {kind === "staff" && (
          <div>
            <div className="font-bold text-[16px] text-zephyr-teal-dark mb-4">New Zephyr Staff</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  First Name
                </label>
                <input
                  value={newForm.first_name}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { first_name: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Last Name
                </label>
                <input
                  value={newForm.last_name}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { last_name: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Email * (login)
                </label>
                <input
                  value={newForm.email}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { email: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px]">
                    Password *
                  </label>
                  <button
                    type="button"
                    onClick={function () {
                      const pw = generatePassword();
                      setNewForm(function (f) {
                        return Object.assign({}, f, { password: pw });
                      });
                      setShowPass(true);
                    }}
                    className="text-[11px] font-bold text-zephyr-teal-dark bg-white border border-zephyr-border rounded-[6px] px-2 py-[2px] cursor-pointer hover:bg-[#EEF3F2]"
                    title="Fill a strong random password (min 12 chars: upper, lower, number, symbol)"
                  >
                    Generate
                  </button>
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPass ? "text" : "password"}
                    value={newForm.password}
                    onChange={function (e) {
                      setNewForm(function (f) {
                        return Object.assign({}, f, { password: e.target.value });
                      });
                    }}
                    className="w-full py-2 px-10 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                    style={{ fontFamily: "inherit" }}
                    placeholder="Min 12 chars"
                  />
                  <button
                    type="button"
                    onClick={function () {
                      setShowPass(function (v) {
                        return !v;
                      });
                    }}
                    className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-[16px] text-zephyr-muted p-0"
                  >
                    {showPass ? "🙈" : "👁"}
                  </button>
                </div>
                <div className="text-[11px] text-zephyr-muted mt-[3px]">
                  Min 12 chars · 1 uppercase · 1 number
                </div>
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Role
                </label>
                <select
                  value={newForm.role !== "shelter" ? newForm.role : "staff"}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { role: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                >
                  {["staff", "picker", "admin", "superuser", "combined"].map(function (r) {
                    return (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Login Method
                </label>
                <div
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-muted bg-[#EEF3F2]"
                  style={{ fontFamily: "inherit" }}
                  title="Set automatically by the email domain — not editable."
                >
                  {import.meta.env.VITE_GOOGLE_SSO === "true" && isZephyr
                    ? "Google Workspace SSO"
                    : "Password + Authenticator"}
                </div>
                <div className="text-[10px] text-zephyr-muted mt-1 normal-case leading-snug">
                  Set automatically by the email domain: a Zephyr Workspace address uses Google SSO,
                  everyone else uses password + authenticator. Not editable.
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-[13px] cursor-pointer mb-1">
              <input
                type="checkbox"
                checked={sendWelcome}
                onChange={function (e) {
                  setNewForm(function (f) {
                    return Object.assign({}, f, { send_welcome: e.target.checked });
                  });
                }}
              />
              <span>Send New Account email</span>
              <span className="text-[11px] text-zephyr-muted">
                (welcome + set-password link; default off for @zephyreducation.com.au)
              </span>
            </label>
            {/* Parity with the Zephyr Staff edit form: whether this staffer appears
                on the Resources → Staff List (db-105). Defaults on. */}
            <label className="flex items-center gap-2 text-[13px] cursor-pointer mb-1">
              <input
                type="checkbox"
                checked={newForm.show_on_staff_list !== false}
                onChange={function (e) {
                  setNewForm(function (f) {
                    return Object.assign({}, f, { show_on_staff_list: e.target.checked });
                  });
                }}
              />
              <span>Show on Staff List</span>
            </label>
            {/* Consultant — a Zephyr person on a non-Zephyr email domain (db-139).
                Flags them as a ZEPHYR person, so the wall never mistakes them for a
                DFV Service contact. See docs/staff-caseworker-separation-plan.md §4. */}
            <label className="flex items-center gap-2 text-[13px] cursor-pointer">
              <input
                type="checkbox"
                checked={newForm.is_consultant === true}
                onChange={function (e) {
                  setNewForm(function (f) {
                    return Object.assign({}, f, { is_consultant: e.target.checked });
                  });
                }}
              />
              <span>Consultant</span>
              <span className="text-[11px] text-zephyr-muted">
                — a Zephyr person whose email is not @zephyreducation.com.au
              </span>
            </label>
          </div>
        )}

        {/* SHELTER form — org record only, no login account */}
        {kind === "shelters" && (
          <div>
            <div className="font-bold text-[16px] text-zephyr-teal-dark mb-1">New DFV Service</div>
            <div className="text-[13px] text-zephyr-muted mb-4">
              Creates an organisation record. Add caseworker login accounts separately via the
              Caseworkers tab.
            </div>

            <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-2">
              Organisation
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  DFV Service Name *
                </label>
                <input
                  value={newForm.shelter_name}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { shelter_name: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  DFV Service Code *
                </label>
                <input
                  value={newForm.shelter_code}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { shelter_code: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="e.g. ZephyrWA001"
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  State
                </label>
                <select
                  value={newForm.state}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { state: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                >
                  <option value="">— Select —</option>
                  {STATES.map(function (s) {
                    return <option key={s}>{s}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Customer Type
                </label>
                <select
                  value={newForm.customer_type}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { customer_type: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                >
                  <option value="">— Select —</option>
                  {customerTypes.map(function (t) {
                    return <option key={t}>{t}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Sales Order Group
                </label>
                <select
                  value={newForm.sales_group}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { sales_group: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                >
                  <option value="">— Select —</option>
                  {salesGroups.map(function (g) {
                    return <option key={g}>{g}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Taxable
                </label>
                <select
                  value={newForm.taxable !== false ? "true" : "false"}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { taxable: e.target.value === "true" });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Phone
                </label>
                <input
                  value={newForm.phone || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { phone: e.target.value });
                    });
                  }}
                  onBlur={function (e) {
                    const v = formatPhone(e.target.value, "landline");
                    setNewForm(function (f) {
                      return Object.assign({}, f, { phone: v });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="e.g. 08 9123 4567"
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Email
                </label>
                <input
                  value={newForm.email || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { email: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="org@example.com"
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  ABN
                </label>
                <input
                  value={newForm.abn || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { abn: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="e.g. 12 345 678 901"
                />
              </div>
            </div>

            <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-2">
              Postal Address
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="col-span-full">
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Street Address
                </label>
                <input
                  value={newForm.postal_line1 || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { postal_line1: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Suburb/City
                </label>
                <input
                  value={newForm.postal_suburb_city || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { postal_suburb_city: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Postcode
                </label>
                <input
                  value={newForm.postal_postcode || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { postal_postcode: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                Internal Notes
              </label>
              <textarea
                value={newForm.internal_notes || ""}
                onChange={function (e) {
                  setNewForm(function (f) {
                    return Object.assign({}, f, { internal_notes: e.target.value });
                  });
                }}
                placeholder="Internal notes not visible to DFV Service users..."
                rows={2}
                className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white resize-y"
                style={{ fontFamily: "inherit" }}
              />
            </div>

            <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-2">
              Default Delivery Address
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Address Name — required with a street; prints on the delivery (no more
                  auto "Default Delivery"). Rule + wording: services/addressName.ts. */}
              <div className="col-span-2">
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Address Name *
                </label>
                <input
                  value={newForm.delivery_address_name || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { delivery_address_name: e.target.value });
                    });
                  }}
                  aria-describedby="new-user-addr-name-help"
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder={ADDRESS_NAME_PLACEHOLDER}
                />
                <AddressNameHelp id="new-user-addr-name-help" />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Street Address *
                </label>
                <AddressAutocomplete
                  mapsReadyTick={mapsReadyTick}
                  value={newForm.delivery_line1 || ""}
                  onChange={function (val) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { delivery_line1: val });
                    });
                  }}
                  onSelect={function (parts) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, {
                        delivery_line1: parts.street,
                        delivery_line2: parts.street2 || f.delivery_line2,
                        delivery_suburb: parts.suburb || f.delivery_suburb,
                        delivery_state: parts.state || f.delivery_state,
                        delivery_postcode: parts.postcode || f.delivery_postcode,
                      });
                    });
                  }}
                  placeholder="Start typing delivery address..."
                  inputStyle={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid #CFE0DE",
                    fontSize: 13,
                    color: "#1A2E2B",
                    fontFamily: "inherit",
                    background: "#FFFFFF",
                  }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Street Address 2
                </label>
                <input
                  value={newForm.delivery_line2 || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { delivery_line2: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Suburb/City
                </label>
                <input
                  value={newForm.delivery_suburb || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { delivery_suburb: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  State
                </label>
                <input
                  value={newForm.delivery_state || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { delivery_state: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div>
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Postcode
                </label>
                <input
                  value={newForm.delivery_postcode || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { delivery_postcode: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div className="col-span-full">
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Delivery Instructions
                </label>
                <input
                  value={newForm.delivery_instructions || ""}
                  onChange={function (e) {
                    setNewForm(function (f) {
                      return Object.assign({}, f, { delivery_instructions: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                  placeholder="e.g. Buzz intercom 2, leave at front desk"
                />
              </div>
            </div>
          </div>
        )}

        {newErr && (
          <div className="bg-[#FFEBEE] text-[#C62828] rounded-[8px] py-[10px] px-[14px] text-[13px] mb-3">
            {newErr}
          </div>
        )}
        <div className="flex gap-[10px] justify-end">
          <Btn
            variant="secondary"
            onClick={function () {
              onClose();
              setNewErr("");
            }}
          >
            Cancel
          </Btn>
          <Btn
            onClick={function () {
              // Block an unsendable address before anything is created
              // (email-validation-plan §6A). Blank is left to createUser's own rules.
              const typed = (newForm.email || "").trim();
              if (kind !== "shelters" && typed && !isValidEmail(typed)) {
                setNewErr(
                  `"${typed}" is not a valid email address. Please check it and try again.`
                );
                return;
              }
              createUser(kind);
            }}
            disabled={newSaving}
          >
            {newSaving
              ? "Creating..."
              : kind === "shelters"
                ? "Create DFV Service"
                : noLoginContact
                  ? "Create Contact"
                  : "Create User"}
          </Btn>
        </div>
      </div>
    </ModalBackdrop>
  );
}
